/**
 * Paramètres qu'un endpoint accepte réellement comme défaut de contexte
 * conversationnel (personne, sprint, lot, échéance posés via `session.setContext`).
 *
 * Partagé entre `VoiceGuidedFlowService` (récapitulatif avant exécution) et
 * `VoiceService` (complétion du chemin GPT) : les deux chemins doivent appliquer
 * exactement les mêmes règles, sinon une même phrase donnerait un résultat
 * différent selon qu'elle passe par un bouton ou par le texte libre.
 *
 * Un endpoint absent de cette table n'accepte aucun défaut — `project.createProject`
 * par exemple, dont le handler ignore ces champs : les proposer laisserait croire
 * à un rattachement qui n'aurait pas lieu.
 */
export const CONTEXT_DEFAULT_PARAMS_BY_ENDPOINT: Partial<Record<string, string[]>> = {
  'project.createTask': ['assigneeName', 'dueDate', 'groupName', 'releaseName']
};

/**
 * Actions mises en avant selon la page ouverte (MOKA-208 P4).
 *
 * Proposer « Réserver une salle » à quelqu'un qui consulte un projet, c'est lui
 * faire lire cinq libellés pour en écarter quatre. La page dit déjà ce que la
 * personne est en train de faire : on s'en sert.
 *
 * Les identifiants sont ceux de `QUICK_ACTIONS` ; l'ordre est celui d'affichage.
 * Une clé absente (ou un module inconnu) retombe sur les actions `primary` du
 * catalogue — le comportement d'avant.
 */
export const CONTEXTUAL_QUICK_ACTIONS: Record<string, string[]> = {
  project: ['createTask', 'assignTask', 'updateTaskStatus', 'projectSummary', 'myTasks'],
  calendar: ['addToCalendar', 'myTasks', 'createTask'],
  rooms: ['reserveRoom', 'addToCalendar', 'myTasks'],
  inventory: ['reportClaim', 'myTasks', 'createTask']
};


/**
 * Endpoints qu'une même phrase peut enchaîner (MOKA-208 P4).
 *
 * Volontairement limité aux créations : elles sont additives, sans effet de
 * bord sur l'existant, et une erreur se corrige en supprimant la tâche créée.
 * Regrouper une suppression ou un changement de statut ferait dépendre plusieurs
 * modifications d'un seul « oui » — et d'une seule interprétation du modèle.
 *
 * MOKA-266 : une réservation de salle est aussi une création (annulable depuis
 * l'écran Salles) — « réserve mardi … et jeudi … » pose les deux créneaux.
 */
export const BATCHABLE_ENDPOINTS: string[] = ['project.createTask', 'project.createProject', 'rooms.reserveRoom'];

/** Actions supplémentaires acceptées au-delà de la première, dans une phrase multiple. */
export const MAX_BATCH_EXTRA = 3;
